/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Formats a numeric value to a significant number of decimal places and returns a `string`.
 *
 * When the fraction digits contain leading 0s, the resulting number is guaranteed to have at least one meaningful non-zero number.
 *
 * This function also applies rounding of significant fraction digits.
 *
 * Examples:
 *
 * ```js
 * formatWithSignificantFractionDigits(3.92, 1) // "3.9"
 *
 * formatWithSignificantFractionDigits(10.99, 1) // "11"
 *
 * formatWithSignificantFractionDigits(3, 2) // "3"
 *
 * formatWithSignificantFractionDigits(0.456, 2) // "0.46"
 *
 * formatWithSignificantFractionDigits(0.123, 2) // "0.12"
 *
 * formatWithSignificantFractionDigits(0.4, 10) // "0.4"
 * ```
 *
 * @param value The numeric value to be formatted.
 * @param digits The amount of significant fraction digits.
 */
// prettier-ignore
export function formatWithSignificantFractionDigits(
  value: number | string | null | undefined, 
  significantFractionDigitPlaces: number
): string {
  if (typeof value === 'undefined' || value === null) return '';

  const originalValueDigits = Number(value).toString(); // This gets rid of trailing 0s like 3.0 => 3, 3.100 => 3.1
  const [integerDigits, fractionDigits] = originalValueDigits.split('.');
  
  if (typeof fractionDigits === 'undefined') return originalValueDigits;
  const [_0, leadingZeroDigits, nonLeadingZeroDigits] = /^(0*)([0-9]*)$/.exec(fractionDigits);
  
  if (fractionDigits.length >= significantFractionDigitPlaces) {
    // We perform truncation of the fraction digits, if we find that only 0s will be taken, we add an extra significant digit
    const significantNonLeadingZeroDigits = leadingZeroDigits.length < significantFractionDigitPlaces
      ? nonLeadingZeroDigits.substring(0, significantFractionDigitPlaces - leadingZeroDigits.length)
      : nonLeadingZeroDigits.substring(0, 1); // An extra significant digit is added
    
    const nextFractionDigit = leadingZeroDigits.length < significantFractionDigitPlaces
      ? nonLeadingZeroDigits.charAt(significantFractionDigitPlaces - leadingZeroDigits.length)
      : nonLeadingZeroDigits.charAt(1);
    
    // We do "rounding" of the truncated significant fraction digits and we add it to the original integer so that
    // Using 2 places 40.024 => 40.02, 0.059 => 0.06, 2.95 => 2.95
    // Using 1 place 40.024 => 40.02, 0.059 => 0.06, 2.95 => 3.0
    if (nextFractionDigit && Number(nextFractionDigit) >= 5) {
      const n = leadingZeroDigits.length + significantNonLeadingZeroDigits.length;
      const m = Math.pow(10, n); // If n is 1 => 10, n is 2 => 100, n is 3 => 1000 and so on

      const a = Number(`0.${leadingZeroDigits}${significantNonLeadingZeroDigits}`);
      const b = Number(`0.${Array(n - 1).fill(0).join('')}1`); // If n is 1 => 0.1, n is 2 => 0.01, n is 3 => 0.001
      const i = Number(`${integerDigits}.${Array(n).fill(0).join('')}`);

      const x = (Math.round(a * m) + Math.round(b * m)) / m; // "Precise" rounding of fraction digits
      const y = ((Math.round(i * m) + Math.round(x * m)) / m).toString(); // We take the original integer and we add the rounded fraction
      
      return y;
    }
    
    return `${integerDigits}.${leadingZeroDigits}${significantNonLeadingZeroDigits}`;
  }

  return `${originalValueDigits}`;
}
